/**
 * Attendance (owner, 13 Oct 2026): what the screens read from /attendance,
 * the one summary query the menu, the reminder bar and the page share, and
 * the two marks — in and out — with the phone's location riding along when
 * it is shared. Attendance belongs to no book: every call says so.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { managesAttendance, managesSalary, marksAttendance, type AttendanceSummary, type GeoPoint, type TodayState } from '@bright/shared';

import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { readLocation } from '@/lib/location';
import { missAdvice } from '@/components/ProofCapture';
import { useToast } from '@/components/ui';

export const attendanceKeys = {
  all: ['attendance'] as const,
  summary: ['attendance', 'summary'] as const,
  me: (month: string) => ['attendance', 'me', month] as const,
  person: (userId: string, month: string) => ['attendance', 'person', userId, month] as const,
  today: ['attendance', 'today'] as const,
  register: (month: string) => ['attendance', 'register', month] as const,
  leave: (status: string) => ['attendance', 'leave', status] as const,
  holidays: ['attendance', 'holidays'] as const,
  policy: ['attendance', 'policy'] as const,
  timings: ['attendance', 'timings'] as const,
};

export type AttendanceRole = 'manager' | 'employee' | null;

/** Whether this sign-in has attendance at all: the owner or an attendance admin who keeps the register, or an employee who marks their day. */
export function useAttendanceRole(): AttendanceRole {
  const { user } = useAuth();
  if (!user) return null;
  if (managesAttendance(user)) return 'manager';
  return marksAttendance({ roleKey: user.role.key, email: user.email, seatOfId: user.seatOf?.id }) ? 'employee' : null;
}

/** Whether this sign-in sets salaries and pays — the head-admin desks alone, whatever the attendance switch says. */
export function useSalaryRole(): boolean {
  const { user } = useAuth();
  return Boolean(user) && managesSalary(user);
}

/** An attendance admin who is also an employee marks their own day too. */
export function useMarksOwnDay(): boolean {
  const { user } = useAuth();
  return Boolean(user) && marksAttendance({ roleKey: user!.role.key, email: user!.email, seatOfId: user!.seatOf?.id });
}

/** Today's state and the leave waiting — for the menu and the reminder bar, refreshed while the app is open. */
export function useAttendanceSummary() {
  const role = useAttendanceRole();
  return useQuery({
    queryKey: attendanceKeys.summary,
    queryFn: () => api.get<AttendanceSummary>('/attendance/summary', undefined, { book: null }),
    enabled: role !== null,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Where the phone is, if it will say. A refusal, a timeout or a desktop with no
 * sense of place all answer null — never a reason to hold up the mark.
 * `maximumAgeMs` is how old a reading the phone may hand back. The time spent
 * on the browser's own "Allow location?" question is not taken from
 * `timeoutMs` (28 Oct 2026, `lib/location.ts`).
 */
export async function captureLocation(timeoutMs = 8_000, maximumAgeMs = 60_000): Promise<GeoPoint | null> {
  return (await readLocation({ maximumAgeMs, preciseMs: timeoutMs, roughMs: 0, roughAgeMs: 0 })).point;
}

/** "09:58" in India for an instant. */
export function clockOf(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(new Date(iso).getTime() + 330 * 60_000).toISOString().slice(11, 16);
}

/** "Tue 14 Oct" for a day key. */
export function dayLabelOf(key: string, withWeekday = true): string {
  return new Date(`${key}T00:00:00Z`).toLocaleDateString('en-IN', { ...(withWeekday ? { weekday: 'short' as const } : {}), day: 'numeric', month: 'short', timeZone: 'UTC' });
}

/** "Monday, 19 October" for a day key — the one line a screen is about. */
export function longDayLabelOf(key: string): string {
  return new Date(`${key}T00:00:00Z`).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
}

/** "9 Oct – 14 Oct" or one day. */
export function spanLabel(from: string, to: string): string {
  return from === to ? dayLabelOf(from) : `${dayLabelOf(from, false)} – ${dayLabelOf(to, false)}`;
}

/** Mark in or out — the location is asked for first, then the server is told. */
export function useMark(kind: 'in' | 'out') {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async () => {
      const read = await readLocation({ maximumAgeMs: 60_000, preciseMs: 8_000, roughMs: 0, roughAgeMs: 0 });
      const where = read.point;
      // A mark-in is not taken without where the phone is (3 Nov 2026) — say how to let it through.
      if (kind === 'in' && !where) {
        const advice = missAdvice(read.miss);
        throw new ApiError({ code: 'VALIDATION_FAILED', status: 422, message: `Location is needed to mark in. ${[advice.lead, ...advice.steps, advice.then].join(' ')}` });
      }
      // Two literal paths, not one template: the contract test reads what the screen asks for.
      if (kind === 'in') return api.post<TodayState>('/attendance/mark-in', { where }, { book: null });
      return api.post<TodayState>('/attendance/mark-out', { where }, { book: null });
    },
    onSuccess: (state) => {
      void queryClient.invalidateQueries({ queryKey: attendanceKeys.all });
      if (kind === 'in') toast.success(`Marked in at ${clockOf(state.inAt)}`, state.late ? 'That counts as late by the office rules.' : undefined);
      else toast.success(`Marked out at ${clockOf(state.outAt)}`, state.hours !== null ? `${state.hours} hours today.` : undefined);
    },
    onError: (cause) => toast.error(kind === 'in' ? 'Not marked in' : 'Not marked out', cause instanceof ApiError ? cause.message : 'Try again in a moment.'),
  });
}
